import moment from 'moment';

// All customer-facing times in this business are WITA (Asia/Makassar), a fixed
// UTC+8 zone with no DST, so moment's own `utcOffset` is enough and this repo
// needs no moment-timezone (which it does not ship).
//
// The server runs UTC and the browser runs whatever the admin's machine says,
// so anything that formats, groups or bounds a day goes through here. A
// local-time day boundary lands eight hours apart on the two sides, which
// corrupts server-rendered markup at hydration and mis-widens date filters.
const WITA_OFFSET_MINUTES = 8 * 60;

/**
 * An instant as a moment in WITA, or null when there is nothing to read - so a
 * missing timestamp formats as nothing rather than as "Invalid date".
 *
 * A plain `YYYY-MM-DD` day parses as UTC midnight, which means `.startOf('day')`
 * and `.endOf('day')` on the result give the instants that WITA calendar day
 * spans. Callers get a normal moment, so formatting is theirs to choose.
 *
 * The input goes through Date first because moment's non-ISO fallback parser is
 * deprecated and warns.
 *
 * @param {Date|string|number} dateLike an instant, or a `YYYY-MM-DD` day
 * @returns {Object|null} a moment fixed to UTC+8, or null
 */
export function wita(dateLike) {
    if (dateLike === null || dateLike === undefined || dateLike === '') return null;
    const instant = new Date(dateLike);
    if (Number.isNaN(instant.getTime())) return null;
    return moment(instant).utcOffset(WITA_OFFSET_MINUTES);
}

/**
 * The legacy "decimal clock" the Booking.*Time columns use: 18:43 -> 18.43,
 * minute-padded so ordering works (14:04 -> 14.04, never 14.4 which sorts as
 * 14:40).
 *
 * Do NOT persist this anywhere new — it exists only so the GraphQL fields
 * deployed clients already read can be DERIVED from the real timestamp columns.
 * Kept as its own function because those resolvers fall back with `??`, which a
 * NaN would slip past.
 *
 * @param {Date|string|number} dateLike an instant
 * @returns {Number|null} WITA wall clock as a padded decimal, or null
 */
export function witaClockDecimal(dateLike) {
    const witaTime = wita(dateLike);
    return witaTime ? parseFloat(witaTime.format('HH.mm')) : null;
}
